import {
  type InsuranceBuilder,
  getActionableInsurances,
  setConfirmedInsurances,
  setQueuedInsurance,
  setUnprovidedInsurance,
  setUnverifiedInsurances,
  setVerifiedInsurances,
} from '@/types/insurance-builder.type.ts';
import {
  type CarrierType,
  PLAN_FAMILY,
  PLAN_FAMILY_ORDER,
  type PlanFamily,
  type QueuedInsurance,
  type UnprovidedInsurance,
  type UnverifiedInsurance,
  type VerifiedInsurance,
  carrierTypeToFamily,
  planFamilyToCarrierTypes,
} from '@/types/insurance.type.ts';

// Resolve outcome types for the 3-way branching in the resolve flow
type ResolveOutcome = 'ask_all' | 'ask_managed' | 'confirm_managed';
type ResolveOverride = 'ask_all' | 'ask_managed';
type InsuranceIdentity = Pick<QueuedInsurance, 'carrier' | 'memberId'>;

/**
 * Central route decision after a family has been answered or verified.
 */
export type PostFamilyResolutionDestination =
  | { type: 'resolve'; planFamily: PlanFamily; previousPlanFamily: PlanFamily }
  | { type: 'benefits' }
  | { type: 'ineligible'; reason: 'out_of_network' };

/**
 * Back navigation from /insurance/resolve can come from either another family,
 * the upload fallback, or the patient info step.
 */
export type ResolveBackDestination =
  | { type: 'upload'; planType: CarrierType }
  | { type: 'resolve'; planFamily: PlanFamily; override?: ResolveOverride }
  | { type: 'patient-info' };

/**
 * Determines which UI to show for a plan family based on discovery-backed coverage evidence.
 * - ask_all: No coverage found, ask user what they have (No / Base / Managed)
 * - ask_managed: Base found (Original Medicare/FFS Medicaid), ask if they have managed plan
 * - confirm_managed: Managed plan found by discovery, show it and ask user to confirm
 */
export function getResolveOutcome(
  verifiedInsurances: VerifiedInsurance[],
  family: PlanFamily
): ResolveOutcome {
  const discoveryTypes = new Set(
    verifiedInsurances
      .filter((insurance) => insurance.resolveSource === 'discovery')
      .map((insurance) => insurance.carrier.carrierType)
  );
  const { base, managed } = planFamilyToCarrierTypes(family);

  if (discoveryTypes.has(managed)) return 'confirm_managed';
  if (discoveryTypes.has(base)) return 'ask_managed';
  return 'ask_all';
}

/**
 * Resolve prompts can review discovery evidence even after the active coverage
 * for a family was replaced by manual verification or upload fallback.
 */
export function getReviewInsurancesForFamily(
  builder: InsuranceBuilder,
  family: PlanFamily
): VerifiedInsurance[] {
  const familyTypes = new Set<CarrierType>(Object.values(planFamilyToCarrierTypes(family)));
  const discoveredInsurances =
    builder.discoveredInsurances.length > 0
      ? builder.discoveredInsurances
      : builder.verifiedInsurances.filter((insurance) => insurance.resolveSource === 'discovery');

  const reviewInsurances = new Map<CarrierType, VerifiedInsurance>();

  for (const insurance of builder.verifiedInsurances) {
    if (familyTypes.has(insurance.carrier.carrierType)) {
      reviewInsurances.set(insurance.carrier.carrierType, insurance);
    }
  }

  for (const insurance of discoveredInsurances) {
    if (familyTypes.has(insurance.carrier.carrierType)) {
      reviewInsurances.set(insurance.carrier.carrierType, insurance);
    }
  }

  return [...reviewInsurances.values()];
}

/**
 * Gets the next plan family that needs to be resolved.
 * Returns undefined if all families have been resolved.
 */
function getNextUnresolvedFamily(builder: InsuranceBuilder): PlanFamily | undefined {
  return PLAN_FAMILY_ORDER.find((family) => !builder.resolvedFamilies.includes(family));
}

/**
 * Used to rebuild breadcrumbs when entering an already-resolved family, such as
 * Benefits -> Medicaid -> Back to Medicare.
 */
export function getPreviousResolvedFamily(
  builder: InsuranceBuilder,
  family: PlanFamily
): PlanFamily | undefined {
  const familyIndex = PLAN_FAMILY_ORDER.indexOf(family);
  if (familyIndex <= 0) return undefined;

  const previousFamily = PLAN_FAMILY_ORDER[familyIndex - 1];
  if (!previousFamily || !builder.resolvedFamilies.includes(previousFamily)) return undefined;

  return previousFamily;
}

/**
 * Used by terminal-ish screens, like benefits/send-link, to return to the latest
 * family the user can review.
 */
function getLastResolvedFamily(builder: InsuranceBuilder): PlanFamily | undefined {
  for (let index = PLAN_FAMILY_ORDER.length - 1; index >= 0; index -= 1) {
    const family = PLAN_FAMILY_ORDER[index];
    if (family && builder.resolvedFamilies.includes(family)) {
      return family;
    }
  }

  return undefined;
}

/**
 * Fallback family when a route needs to recover without an explicit planFamily.
 * Prefer unfinished work, then the latest answered family, then the first family.
 */
export function getDefaultResolveFamily(builder: InsuranceBuilder): PlanFamily {
  return getNextUnresolvedFamily(builder) ?? getLastResolvedFamily(builder) ?? PLAN_FAMILY.MEDICARE;
}

/**
 * Marks a plan family as resolved and optionally as covered.
 */
export function resolveFamily(
  builder: InsuranceBuilder,
  family: PlanFamily,
  hasCoverage: boolean
): InsuranceBuilder {
  const resolvedFamilies = builder.resolvedFamilies.includes(family)
    ? builder.resolvedFamilies
    : [...builder.resolvedFamilies, family];

  let coveredFamilies: PlanFamily[];
  if (hasCoverage) {
    coveredFamilies = builder.coveredFamilies.includes(family)
      ? builder.coveredFamilies
      : [...builder.coveredFamilies, family];
  } else {
    coveredFamilies = builder.coveredFamilies.filter((f) => f !== family);
  }

  return {
    ...builder,
    resolvedFamilies,
    coveredFamilies,
  };
}

/**
 * Records a user's yes/no answer for a family. A "no" answer also clears stale
 * insurance records for that family so old verified coverage cannot leak into
 * benefits after the user changes their mind.
 */
export function answerFamily(
  builder: InsuranceBuilder,
  family: PlanFamily,
  hasCoverage: boolean
): InsuranceBuilder {
  const answeredBuilder = resolveFamily(builder, family, hasCoverage);

  return hasCoverage ? answeredBuilder : removeFamilyInsuranceRecords(answeredBuilder, family);
}

/**
 * Queues manually entered insurance for verification.
 */
export function queueFamilyInsurance(
  builder: InsuranceBuilder,
  insurance: QueuedInsurance
): InsuranceBuilder {
  return preserveFamilyEditEvidence(builder, setQueuedInsurance(builder, insurance), {
    carrierType: insurance.carrier.carrierType,
    confirmedInsuranceIdentity: insurance,
  });
}

/**
 * Records that the user cannot provide insurance details for this family.
 */
export function markFamilyUnprovided(
  builder: InsuranceBuilder,
  insurance: UnprovidedInsurance
): InsuranceBuilder {
  return preserveFamilyEditEvidence(builder, setUnprovidedInsurance(builder, insurance), {
    carrierType: insurance.carrierType,
  });
}

/**
 * Saves failed-verification insurance while keeping any discovered base plan for
 * the same family, so retry/back flows return to the found-coverage prompt.
 */
export function setUnverifiedFamilyInsurances(
  builder: InsuranceBuilder,
  ...insurances: UnverifiedInsurance[]
): InsuranceBuilder {
  const updatedBuilder = setUnverifiedInsurances(builder, ...insurances);

  return insurances.reduce(
    (acc, insurance) =>
      preserveFamilyEditEvidence(builder, acc, {
        carrierType: insurance.carrier.carrierType,
        confirmedInsuranceIdentity: insurance,
      }),
    updatedBuilder
  );
}

/**
 * Saves verified insurance while keeping any discovered base plan for the same
 * family. This lets a manually verified managed plan coexist with the discovery
 * evidence that drives the "Do you also have..." follow-up if the user edits
 * that family again.
 */
export function setVerifiedFamilyInsurances(
  builder: InsuranceBuilder,
  ...insurances: VerifiedInsurance[]
): InsuranceBuilder {
  const updatedBuilder = setVerifiedInsurances(builder, ...insurances);

  return insurances.reduce(
    (acc, insurance) =>
      preserveFamilyEditEvidence(builder, acc, { carrierType: insurance.carrier.carrierType }),
    updatedBuilder
  );
}

/**
 * Saves uploaded-card confirmation while keeping any discovered base plan for
 * the same family.
 */
export function setConfirmedFamilyInsurances(
  builder: InsuranceBuilder,
  ...insurances: UnverifiedInsurance[]
): InsuranceBuilder {
  const updatedBuilder = setConfirmedInsurances(builder, ...insurances);

  return insurances.reduce(
    (acc, insurance) =>
      preserveFamilyEditEvidence(builder, acc, { carrierType: insurance.carrier.carrierType }),
    updatedBuilder
  );
}

/**
 * Marks later families as needing review after an earlier family changes,
 * without deleting insurance evidence gathered for those families.
 */
export function markLaterFamiliesUnresolved(
  builder: InsuranceBuilder,
  family: PlanFamily
): InsuranceBuilder {
  const familyIndex = PLAN_FAMILY_ORDER.indexOf(family);
  const laterFamilies = PLAN_FAMILY_ORDER.slice(familyIndex + 1);

  if (laterFamilies.length === 0) return builder;

  return {
    ...builder,
    resolvedFamilies: builder.resolvedFamilies.filter(
      (resolvedFamily) => !laterFamilies.includes(resolvedFamily)
    ),
    coveredFamilies: builder.coveredFamilies.filter(
      (coveredFamily) => !laterFamilies.includes(coveredFamily)
    ),
  };
}

/**
 * Returns true if all plan families have been resolved.
 */
export function allFamiliesResolved(builder: InsuranceBuilder): boolean {
  return PLAN_FAMILY_ORDER.every((family) => builder.resolvedFamilies.includes(family));
}

/**
 * Returns the next route after the current family is complete.
 */
export function getPostFamilyResolutionDestination(
  builder: InsuranceBuilder,
  currentFamily: PlanFamily
): PostFamilyResolutionDestination {
  const nextFamily = getNextUnresolvedFamily(builder);
  if (nextFamily) {
    return { type: 'resolve', planFamily: nextFamily, previousPlanFamily: currentFamily };
  }

  if (hasActionableCoverage(builder)) {
    return { type: 'benefits' };
  }

  return { type: 'ineligible', reason: 'out_of_network' };
}

/**
 * Normalizes Back behavior for /insurance/resolve so the component doesn't need
 * to know whether it came from upload, another family, or patient info.
 */
export function getResolveBackDestination({
  previousPlanFamily,
  previousUploadPlanType,
}: {
  previousPlanFamily?: PlanFamily;
  previousUploadPlanType?: CarrierType;
}): ResolveBackDestination {
  if (previousUploadPlanType) {
    return { type: 'upload', planType: previousUploadPlanType };
  }

  if (previousPlanFamily) {
    return { type: 'resolve', planFamily: previousPlanFamily };
  }

  return { type: 'patient-info' };
}

/**
 * A rejected discovered managed plan uses ask_managed as a temporary correction
 * screen. Back navigation should return to the preserved discovered confirmation
 * instead of making that correction screen the durable breadcrumb.
 */
export function getResolveBackOverride({
  builder,
  family,
  override,
}: {
  builder: InsuranceBuilder;
  family: PlanFamily;
  override?: ResolveOverride;
}): ResolveOverride | undefined {
  if (
    override === 'ask_managed' &&
    getResolveOutcome(getReviewInsurancesForFamily(builder, family), family) === 'confirm_managed'
  ) {
    return undefined;
  }

  return override;
}

/**
 * Returns true if there's at least one covered family with actionable insurance.
 */
export function hasActionableCoverage(builder: InsuranceBuilder): boolean {
  const actionableFamilies = getActionableFamilies(builder);
  return actionableFamilies.size > 0;
}

/**
 * Gets the verified insurance for a specific plan family from discovery results.
 * Returns the managed plan if found, otherwise the base plan.
 */
export function getVerifiedInsuranceForFamily(
  builder: InsuranceBuilder,
  family: PlanFamily
): VerifiedInsurance | undefined {
  const { base, managed } = planFamilyToCarrierTypes(family);

  // Prefer managed plan if found.
  const managedInsurance = builder.verifiedInsurances.find(
    (i) => i.carrier.carrierType === managed
  );
  if (managedInsurance) return managedInsurance;

  // Fall back to base plan.
  return builder.verifiedInsurances.find((i) => i.carrier.carrierType === base);
}

function getActionableFamilies(builder: InsuranceBuilder): Set<PlanFamily> {
  const families = new Set<PlanFamily>();

  for (const insurance of getActionableInsurances(builder)) {
    families.add(carrierTypeToFamily(insurance.carrier.carrierType));
  }

  return families;
}

/**
 * Clears all stored insurance artifacts for one family while preserving the
 * family's resolved/covered answer fields.
 */
function removeFamilyInsuranceRecords(
  builder: InsuranceBuilder,
  family: PlanFamily
): InsuranceBuilder {
  const familyTypes = new Set<CarrierType>(Object.values(planFamilyToCarrierTypes(family)));

  return {
    ...builder,
    queuedInsurances: builder.queuedInsurances.filter(
      (insurance) => !familyTypes.has(insurance.carrier.carrierType)
    ),
    unverifiedInsurances: builder.unverifiedInsurances.filter(
      (insurance) => !familyTypes.has(insurance.carrier.carrierType)
    ),
    unprovidedInsurances: builder.unprovidedInsurances.filter(
      (insurance) => !familyTypes.has(insurance.carrierType)
    ),
    confirmedInsurances: builder.confirmedInsurances.filter(
      (insurance) => !familyTypes.has(insurance.carrier.carrierType)
    ),
    verifiedInsurances: builder.verifiedInsurances.filter(
      (insurance) => !familyTypes.has(insurance.carrier.carrierType)
    ),
  };
}

function preserveFamilyEditEvidence(
  previousBuilder: InsuranceBuilder,
  updatedBuilder: InsuranceBuilder,
  {
    carrierType,
    confirmedInsuranceIdentity,
  }: {
    carrierType: CarrierType;
    confirmedInsuranceIdentity?: InsuranceIdentity;
  }
): InsuranceBuilder {
  const { base, managed } = planFamilyToCarrierTypes(carrierTypeToFamily(carrierType));
  let preservedBuilder = updatedBuilder;

  if (carrierType === managed) {
    const discoveredBaseInsurance = previousBuilder.verifiedInsurances.find(
      (insurance) =>
        insurance.resolveSource === 'discovery' && insurance.carrier.carrierType === base
    );

    const alreadyPreserved = preservedBuilder.verifiedInsurances.some(
      (insurance) =>
        insurance.resolveSource === 'discovery' && insurance.carrier.carrierType === base
    );

    if (discoveredBaseInsurance && !alreadyPreserved) {
      preservedBuilder = {
        ...preservedBuilder,
        verifiedInsurances: [...preservedBuilder.verifiedInsurances, discoveredBaseInsurance],
      };
    }
  }

  if (!confirmedInsuranceIdentity) return preservedBuilder;

  const confirmedInsurance = previousBuilder.confirmedInsurances.find((insurance) =>
    isSameInsuranceIdentity(insurance, confirmedInsuranceIdentity)
  );

  const hasConfirmedInsurance = preservedBuilder.confirmedInsurances.some((insurance) =>
    isSameInsuranceIdentity(insurance, confirmedInsuranceIdentity)
  );

  if (!confirmedInsurance || hasConfirmedInsurance) return preservedBuilder;

  return {
    ...preservedBuilder,
    confirmedInsurances: [...preservedBuilder.confirmedInsurances, confirmedInsurance],
  };
}

function isSameInsuranceIdentity(left: InsuranceIdentity, right: InsuranceIdentity): boolean {
  return (
    left.carrier.internalId === right.carrier.internalId &&
    left.carrier.carrierType === right.carrier.carrierType &&
    left.memberId === right.memberId
  );
}
